import { can, type AuthorizedUser } from '@dma/auth';
import { AuthorizationError, ApplicationError, ValidationError, type Logger } from '@dma/shared';
import { generateRequestId } from '@dma/utils';
import { Article } from '../../domain/entities/Article.entity';
import { InvalidArticleError } from '../../domain/errors/InvalidArticleError';
import type { ArticleRepository } from '../../domain/repositories/ArticleRepository.interface';
import type { SaveArticleDto } from '../dto/SaveArticleDto';
import type { ArticleDto } from '../dto/ArticleDto';

/**
 * Handles both create (input.id === null) and update (input.id set), same
 * precedent as SaveScientificEntryUseCase. Unlike that use case, this one
 * checks granular permissions — 'news:create' vs 'news:update' — because
 * those are the permissions this project's RBAC model already defined
 * (packages/auth/src/permissions.ts) specifically for this feature, unlike
 * scientific-entry's single catch-all 'scientific-entry:manage'. Setting
 * `status: 'published'` additionally requires 'news:publish', independent of
 * create/update — an editor who can draft an article does not automatically
 * get to make it public.
 */
export class SaveArticleUseCase {
  constructor(
    private readonly repository: ArticleRepository,
    private readonly logger: Logger
  ) {}

  public async execute(actingUser: AuthorizedUser | null, input: SaveArticleDto): Promise<ArticleDto> {
    const isUpdate = input.id !== null;

    if (!can(actingUser, isUpdate ? 'news:update' : 'news:create')) {
      throw new AuthorizationError();
    }
    if (input.status === 'published' && !can(actingUser, 'news:publish')) {
      throw new AuthorizationError('شما اجازه‌ی انتشار مطلب را ندارید.');
    }

    const now = new Date().toISOString();
    const existing = isUpdate ? await this.repository.getById(input.id as string) : null;

    if (isUpdate && !existing) {
      throw new ApplicationError('ARTICLE_NOT_FOUND', 'مطلب مورد نظر برای ویرایش یافت نشد.');
    }

    let article: Article;
    try {
      article = Article.create({
        ...input,
        id: existing?.props.id ?? generateRequestId(),
        createdAt: existing?.props.createdAt ?? now,
        updatedAt: now
      });
    } catch (error) {
      if (error instanceof InvalidArticleError) {
        throw new ValidationError(error.message);
      }
      throw error;
    }

    try {
      await this.repository.save(article);
    } catch (error) {
      this.logger.error('Failed to persist article', {
        articleId: article.props.id,
        cause: error instanceof Error ? error.message : String(error)
      });
      throw new ApplicationError('ARTICLE_SAVE_FAILED', 'ذخیره‌ی مطلب با مشکل مواجه شد. لطفاً دوباره تلاش کنید.', {
        cause: error
      });
    }

    return article.props;
  }
}
